import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";

import { cn } from "./cn";

/**
 * Table primitives (shadcn/ui style).
 *
 * Wrapped in a scroll container rather than relying on the page: an Admin table
 * with a title, a status, two dates and a locale is wider than a laptop, and a
 * table that pushes the whole layout sideways is worse than one that scrolls.
 */

export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn("w-full caption-bottom text-sm", className)} {...props} />
    </div>
  );
}

export function TableHeader({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn("border-b border-neutral-200", className)} {...props} />;
}

export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />;
}

export function TableRow({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr className={cn("border-b border-neutral-100 hover:bg-neutral-50", className)} {...props} />
  );
}

export function TableHead({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn("h-10 px-3 text-left align-middle font-medium text-neutral-500", className)}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn("px-3 py-2.5 align-middle", className)} {...props} />;
}

/** Shown in place of rows when a list is empty. */
export interface EmptyStateProps {
  readonly message: string;
  readonly hint?: string;
  readonly children?: ReactNode;
}

export function EmptyState({ message, hint, children }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-neutral-300 p-10 text-center">
      <p className="text-sm font-medium text-neutral-700">{message}</p>
      {hint === undefined ? null : <p className="text-sm text-neutral-500">{hint}</p>}
      {children}
    </div>
  );
}
